import { Chart as G2Chart } from '@antv/g2';
import { forwardRef, HTMLAttributes, Ref, useEffect, useImperativeHandle, useRef } from 'react';

import { IBaseChartProps, IComponent } from '../../../models/components.models';
import { useClassNames } from '../../../utils/hooks';
import { ILineChartClassNames, ILineChartStyles, lineChartStylesDefault } from './LineChart.styles';

const render = (
  el: HTMLElement,
  data: { [key: string]: any }[],
  labelFieldName: string,
  valueFieldNames: string[],
  colors: string[] = []
) => {
  let style: { [key: string]: string } = {};
  if (colors[0]) {
    style = { stroke: colors[0] };
  }
  const chart = new G2Chart({ container: el, autoFit: true });
  chart
    .data(data)
    .encode('x', labelFieldName)
    .encode('y', valueFieldNames[0])
    .scale('x', { range: [0, 1] })
    .scale('y', { domainMin: 0, nice: true });
  chart
    .line()
    .label({
      text: valueFieldNames[0],
      style: {
        dx: -10,
        dy: -12,
      },
    })
    .style(style);
  chart
    .point()
    .style({ fill: 'white', ...style })
    .tooltip(false);
  chart.render();
};

export interface ILineChartProps extends IComponent<ILineChartStyles>, HTMLAttributes<HTMLDivElement>, IBaseChartProps {
  colors?: string[];
}

export const LineChart = forwardRef((props: ILineChartProps, ref: Ref<HTMLDivElement>) => {
  // some variables are not used, but they need to get `elementProps`.
  const { className, styles, data = [], colors, labelFieldName = '', valueFieldNames = [], ...elementProps } = props;
  const refRoot = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  useEffect(() => {
    if (refRoot.current) {
      render(refRoot.current, data, labelFieldName, valueFieldNames, colors);
    }
  }, [data, labelFieldName, valueFieldNames, colors]);

  const classNames = useClassNames<Partial<ILineChartStyles>, ILineChartClassNames>(
    'LineChart',
    lineChartStylesDefault,
    styles,
    className
  );
  return <div {...elementProps} ref={refRoot} className={classNames.root} />;
});
